import React, { useState } from 'react';
import { MasterCardNew, VisaCardNew } from '@mafc/icon';
import { useTranslation } from 'react-i18next';
import { Flex, Box, Text, Input } from '@mafc/trolly';
import COLORS from 'theme/colors';
import ICreditCard from '../CreditCard.interface';
import usePayment from '../hooks/usePayment';
import { CardType } from 'network/response/ICart';
import { getCardTypeForIcon } from '../helpers/payment.helpers';
import { CREDIT_CARD_MASKS } from './NewCardForm/NewCardForm';
import { ReactComponent as IconCVV } from 'assets/svgs/cvv.svg';
import TYPOGRAPHY from 'theme/typography';
import useAction from 'hooks/useAction';
import { storePaymentDetails } from 'pages/Checkout/redux/actions/payment.action';
import EPaymentMethods from '../PamentMethods.enum';

const { Paragraph, Caption } = Text;

interface ICreditCardItemProps {
  card: ICreditCard;
  expired?: boolean;
  creditCardSubtitle?: string;
}

enum cardDiscountType {
  BIN = 'BIN',
}

const CreditCardItem: React.FC<ICreditCardItemProps> = ({
  card: { lastFourDigits, expiryMonth, expiryYear, id, bin, isCVVRequired },
  expired,
  creditCardSubtitle,
}: ICreditCardItemProps) => {
  const [error, setError] = useState<string>('');
  const [cvvValue, setCvvValue] = useState<string>('');
  const expiryDate = `${expiryMonth}/${expiryYear}`;
  const cardType = bin ? getCardTypeForIcon(bin) : '';
  // Translate for multilingual support & rtl check helper
  const { t } = useTranslation();
  const {
    appliedVoucher,
    payment: { paymentDetails },
  } = usePayment();
  const [storePaymentDetailsAction] = useAction(storePaymentDetails);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setError('');
    setCvvValue(value);
  };
  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isValid = value && value.length > 2;
    if (!isValid) {
      setError(t('paymentForm.invalidCvv'));
    }

    storePaymentDetailsAction(EPaymentMethods.CREDIT_CARD, {
      ...paymentDetails,
      saved: true,
      cvvValue: value,
      isValid,
    });
  };
  return (
    <Box>
      <Flex alignItems="center">
        <Box>{cardType === CardType.master ? <MasterCardNew /> : <VisaCardNew />}</Box>
        <Box ml={16}>
          <Box fontWeight={6} color={COLORS.BLACK}>
            {t('savedCard')}
          </Box>
          <Paragraph fontSize={3} color={COLORS.BLACK}>
            **** {lastFourDigits} |{' '}
            {expired ? (
              <Caption color={COLORS.RED} fontSize={1}>
                {t('expiredCard')} ({expiryDate})
              </Caption>
            ) : (
              <Caption fontSize={3} color={COLORS.BLACK}>
                {expiryDate}
              </Caption>
            )}
          </Paragraph>
          {isCVVRequired && (
            <Box width={[1 / 2]} height="65px">
              <Input
                label={t('paymentForm.cvv')}
                value={cvvValue}
                mask={CREDIT_CARD_MASKS.cardCVV}
                name="card_cvn"
                type="password"
                iconRight={<IconCVV />}
                onChange={handleChange}
                onBlur={handleBlur}
                data-testid="__checkout_payments_card_cvv__"
                autoComplete="cc-cvn"
                disabled={false}
                maxLength={3}
                mui
              />
              {error && (
                <Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
                  {error}
                </Paragraph>
              )}
            </Box>
          )}
          {appliedVoucher &&
            appliedVoucher.description &&
            paymentDetails &&
            paymentDetails.id &&
            appliedVoucher.type === cardDiscountType.BIN && (
              <Box color={COLORS.GREEN} fontSize={1}>
                {appliedVoucher.description}
              </Box>
            )}
        </Box>
      </Flex>
      {creditCardSubtitle !== '' && (
        <Box color={COLORS.THUNDORA} fontSize="10px">
          {creditCardSubtitle}
        </Box>
      )}
    </Box>
  );
};

export default CreditCardItem;
